/**
 * Whether a running pour has passed the planned end of its own stop
 * setting, for the desktop Control Panel row and the desktop pour dashboard
 * banner, so a person sees it and stops it by hand. Display only: nothing
 * is ever stopped from it.
 *
 * The planned end is the phone list's rule (components/controlPanel/
 * pourCardModel.plannedEndOf): StopType '31' = XHours DAYS after the start,
 * else the preset stop time. The start is Started, else the logger start.
 * Started is empty on most pours made in the legacy app ("Pour Started" was
 * an optional box there), and then the background service's own '31' stop
 * never fires (services/pour_stop_rules), so such a pour stays In Progress
 * until someone stops it. The desktop list has no concrete start, so the
 * row and the banner both go without it and always agree.
 */
import type { Pour } from '../services/pours';
import { plannedEndOf } from '../components/controlPanel/pourCardModel';
import { formatInTimeZone } from './datetime';
import { formatAgo } from './relativeTime';
import type { TranslateFn } from './relativeTime';

export interface PlannedEndInput {
  /** In Progress and started (not Scheduled): only a running pour can be
   *  past its end. */
  running: boolean;
  stopType?: string | null;
  /** Days for StopType '31'. */
  xHours?: number | null;
  /** Naive UTC backend times. */
  presetStopTime?: string | null;
  started?: string | null;
  loggerStart?: string | null;
}

export interface PastPlannedEnd {
  /** The planned end, epoch ms. */
  end: number;
  /** The planned monitoring length in whole days. */
  days: number;
}

/** The planned end when it is behind `now` (epoch ms), else null. */
export const pastPlannedEnd = (input: PlannedEndInput, now: number): PastPlannedEnd | null => {
  if (!input.running) return null;
  // plannedEndOf reads only the stop setting and the starts of a list row.
  const row: Pour = {
    PourID: '',
    PourName: '',
    StopType: input.stopType ?? undefined,
    XHours: input.xHours ?? undefined,
    PreSetStopTime: input.presetStopTime ?? undefined,
    Started: input.started ?? undefined,
    LoggerStart: input.loggerStart ?? undefined,
  };
  const planned = plannedEndOf(row);
  return planned && planned.end < now ? planned : null;
};

/** "29 Sept 2026" in the pour's timezone. */
export const formatPlannedEndDate = (end: number, timeZone?: string | null): string =>
  formatInTimeZone(end, timeZone, { day: 'numeric', month: 'short', year: 'numeric', hour: undefined, minute: undefined });

/** The sentence the row's tooltip and the dashboard banner share:
 *  "It was planned to end on 29 Sept 2026 (24 d ago), 7 days after it
 *  started." */
export const plannedEndSentence = (
  past: PastPlannedEnd, now: number, timeZone: string | null | undefined, t: TranslateFn,
): string =>
  t('pours.pastPlannedEndSentence', {
    count: past.days,
    defaultValue: 'It was planned to end on {{date}} ({{ago}}), {{count}} days after it started.',
    defaultValue_one: 'It was planned to end on {{date}} ({{ago}}), 1 day after it started.',
    defaultValue_other: 'It was planned to end on {{date}} ({{ago}}), {{count}} days after it started.',
    date: formatPlannedEndDate(past.end, timeZone),
    ago: formatAgo(past.end, now, t),
  });
